import React, { useState } from 'react';
import { CheckProgram } from '@/components/program/CheckProgram';

interface BenefitCard {
    title: string;
    icon: React.ReactNode;
    benefits: string[];
    description: React.ReactNode;
}

const LmsBenefits: React.FC = () => {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [showContent, setShowContent] = useState<boolean>(false);

    const nextCard = () => {
        setActiveIndex((prev) => (prev + 1) % cards.length);
        setShowContent(false);
    };

    const prevCard = () => {
        setActiveIndex((prev) =>
            prev === 0 ? cards.length - 1 : prev - 1
        );
        setShowContent(false);
    };
    const schoolBenefits = [
        'Pusat materi pembelajaran digital',
        'Pengumpulan dan penilaian tugas/ujian',
        'Komunikasi guru-siswa/dosen-mahasiswa (forum, chat, pengumuman)',
        'Monitoring progres belajar siswa/mahasiswa',
        'Mendukung pembelajaran mandiri dan blended learning',
        'Akses belajar fleksibel (kapan saja, di mana saja)',
        'Integrasi dengan sistem akademik (KRS, nilai, presensi)',
        'Mendukung pembelajaran kolaboratif (diskusi kelompok, peer review)',
        'Pelacakan kehadiran digital',
        'Dokumentasi pembelajaran jangka panjang',
        'Umpan balik langsung dari dosen/guru',
        'Penerbitan sertifikat atau bukti kelulusan otomatis'
    ];

    const companyBenefits = [
        'Media pelatihan dan pengembangan karyawan',
        'Akses pelatihan online kapan saja (fleksibel & remote)',
        'Otomatisasi evaluasi dan sertifikasi pelatihan',
        'Monitoring kemajuan dan hasil belajar karyawan',
        'Penyimpanan materi SOP, modul pelatihan, dan dokumentasi internal',
        'Pelatihan onboarding karyawan baru',
        'Pengurangan biaya pelatihan tatap muka',
        'Distribusi pelatihan ke banyak cabang/lokasi sekaligus',
        'Dukungan pembelajaran berkelanjutan (continuous learning)',
        'Integrasi dengan sistem HR (penilaian kerja, promosi, dll)',
        'Personalisasi materi sesuai divisi, jabatan, atau level'
    ];

    const cards: BenefitCard[] = [
        {
            title: "LMS di Sekolah/Universitas",
            icon: (
                <svg className="w-32 h-32 text-accent-100" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                </svg>
            ),
            benefits: schoolBenefits,
            description: (
                <div className="space-y-2 mx-5">
                    <p className="text-3xl font-semibold text-primary-100">
                        Kamu pihak <span className="text-[#FF6B6B]">Sekolah</span>?
                    </p>
                    <p className="text-2xl font-medium">
                        <span className="text-[#20C997] font-bold">LMS</span> bantu sekolahmu lebih terorganisir dan efisien, loh!
                    </p>
                    <p className="text-xl text-gray-500 italic">Yuk kenali manfaatnya!</p>
                </div>
            )
        },
        {
            title: "LMS di Instansi/Perusahaan",
            icon: (
                <svg className="w-32 h-32 text-accent-100" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                </svg>
            ),
            benefits: companyBenefits,
            description: (
                <div className="space-y-2 mx-5">
                    <p className="text-3xl font-semibold text-primary-100">
                        Kamu dari <span className="text-[#F59E0B]">Perusahaan</span>?
                    </p>
                    <p className="text-2xl font-medium">
                        <span className="text-[#20C997] font-bold">LMS</span> bisa bantu pelatihan internal, loh!
                    </p>
                    <p className="text-xl text-gray-500 italic">Yuk kenali manfaatnya!</p>
                </div>
            )
        }
    ];

    const activeCard = cards[activeIndex];

    return (
        <div className="py-8 px-6 md:px-20">
            <div className="max-w-7xl mx-auto">
                
                {/* Desktop View */}
                <div className="hidden md:grid md:grid-cols-2 gap-12">
                    {cards.map((card, index) => (
                        <div key={`desktop-${index}`} className="relative w-full group">
                            <div className="w-full h-[500px] rounded-xl shadow-md border bg-white overflow-hidden relative">
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center group-hover:opacity-0 transition-opacity duration-300 px-6">
                                    <div className="mb-5">{card.icon}</div>
                                    {card.description}
                                </div>
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col overflow-hidden rounded-xl">
                                    <div className="bg-accent-100 py-3 rounded-t-xl">
                                        <h3 className="text-white font-semibold text-lg font-body text-center">{card.title}</h3>
                                    </div>
                                    <div className="flex-1 overflow-y-auto py-4 px-6 space-y-3 text-secondary-100">
                                        {card.benefits.map((benefit, i) => (
                                            <CheckProgram key={`desktop-${index}-${i}`} name={benefit} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Mobile */}
                <div className="md:hidden relative">

                    <div className="relative">
                        <div className="w-full min-h-[580px] rounded-xl shadow-md border bg-white"
                            onClick={() => setShowContent(!showContent)}
                        >

                            {!showContent && activeCard && (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                                    <div className="mb-5">{activeCard.icon}</div>
                                    {activeCard.description}
                                </div>
                            )}

                            {showContent && activeCard && (
                                <div className="absolute inset-0 flex flex-col overflow-hidden">
                                    <div className="bg-accent-100 py-3">
                                        <h3 className="text-white font-semibold text-center">{activeCard.title}</h3>
                                    </div>

                                    <div className="flex-1 overflow-y-auto py-4 px-6 space-y-3">
                                        {activeCard.benefits.map((b, i) => (
                                            <CheckProgram key={i} name={b} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* arrows */}
                        <button
                            onClick={prevCard}
                            className="absolute w-9 h-9 -left-4 top-1/2 -translate-y-1/2 bg-white shadow rounded-full"
                        ><img src="/img/ekskul/left-arrow.png" alt="Previous" className="w-full h-full" /></button>

                        <button
                            onClick={nextCard}
                            className="absolute w-9 h-9 -right-4 top-1/2 -translate-y-1/2 bg-white shadow rounded-full"
                        ><img src="/img/ekskul/right-arrow.png" alt="Next" className="w-full h-full" /></button>

                        {/* dots */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
                            {cards.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => {
                                        setActiveIndex(i);
                                        setShowContent(false);
                                    }}
                                    className={`h-3 w-3 rounded-full transition ${
                                        i === activeIndex ? 'bg-accent-100 scale-125' : 'bg-gray-400'
                                    }`}
                                />
                            ))}
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
};

export default LmsBenefits;
